// DERIVADO DE: gestion-web-cliente@1a5ce2d39
//   src/app/auxiliares/servicios/http/{dispositivos-lorawan,modelos-dispositivos,
//   config-perfil,grupos,puestas,clientes,direcciones}.service.ts
// CAMBIOS: `@Service()`, `HttpClient` directo. Las RUTAS no se tocan (ADR-008).

import { Service, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import type {
  ICliente,
  ICategoriaEvento,
  ICodigosDispositivo,
  IUbicacion,
  IConfigPerfil,
  IDispositivoLorawan,
  IGrupo,
  IListado,
  IModeloDispositivo,
  IPuesta,
  IQueryParam,
  ITipoCliente,
} from 'modelos';

import { queryParams } from '@core/datos/query-params';

abstract class CatalogoHttp<T> {
  protected readonly http = inject(HttpClient);
  protected abstract readonly ruta: string;

  listar(query?: IQueryParam): Promise<IListado<T>> {
    return firstValueFrom(this.http.get<IListado<T>>(this.ruta, { params: queryParams(query) }));
  }

  porId(id: string): Promise<T> {
    return firstValueFrom(this.http.get<T>(`${this.ruta}/${id}`));
  }

  crear(dato: Partial<T>): Promise<T> {
    return firstValueFrom(this.http.post<T>(this.ruta, dato));
  }

  actualizar(id: string, dato: Partial<T>): Promise<T> {
    return firstValueFrom(this.http.put<T>(`${this.ruta}/${id}`, dato));
  }

  eliminar(id: string): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${this.ruta}/${id}`));
  }
}

/** Los nodos LoRaWAN. Una luminaria es una carcasa; el nodo es lo que habla. */
@Service()
export class DispositivosLorawanHttp extends CatalogoHttp<IDispositivoLorawan> {
  protected readonly ruta = '/dispositivoslorawan';
}

/**
 * Modelos de dispositivo.
 *
 * **Hay dos por luminaria y no son lo mismo** (`tipo` los distingue): el modelo
 * del **nodo LoRaWAN** (`DispositivoLorawan`) trae el set de códigos con el que
 * se interpretan sus mensajes; el modelo de la **luminaria** (`Luminaria`) trae
 * el driver eléctrico, o sea los márgenes de potencia y de factor de potencia
 * por nivel de dimerización. Mezclarlos es pedirle los códigos al que tiene el
 * driver.
 */
@Service()
export class ModelosDispositivoHttp extends CatalogoHttp<IModeloDispositivo> {
  protected readonly ruta = '/modelodispositivos';
}

/**
 * Los sets de códigos con los que se interpretan los mensajes de un nodo.
 *
 * Ojo con el plural: la entidad es `ICodigosDispositivo` —el **set**— y cada
 * entrada de su array `codigos` es un `ICodigoDispositivo`.
 */
@Service()
export class CodigosHttp extends CatalogoHttp<ICodigosDispositivo> {
  protected readonly ruta = '/codigosdispositivos';

  /** Copia el set a otro cliente. La ruta es la del clonador genérico de IRIX. */
  clonar(id: string, idCliente: string): Promise<void> {
    return firstValueFrom(
      this.http.post<void>('/clonadorentidades/codigosdispositivos', {
        idCliente,
        idCodigosDispositivos: id,
      }),
    );
  }
}

/** A qué categoría de evento mapea cada código. */
@Service()
export class CategoriasEventoHttp extends CatalogoHttp<ICategoriaEvento> {
  protected readonly ruta = '/categoriaeventos';
}

/** Perfiles de configuración: el dimerizado y el modo que el nodo tiene que tener. */
@Service()
export class PerfilesConfigHttp extends CatalogoHttp<IConfigPerfil> {
  // Sí, camelCase con mayúscula en medio: así está publicada.
  protected readonly ruta = '/configPerfils';
}

@Service()
export class GruposHttp extends CatalogoHttp<IGrupo> {
  protected readonly ruta = '/grupos';
}

@Service()
export class PuestasHttp extends CatalogoHttp<IPuesta> {
  protected readonly ruta = '/puestas';
}

/**
 * Los clientes, que son la jerarquía entera del sistema.
 *
 * Un cliente cuelga de otro por `idPadre`, y `idsAncestros` guarda la cadena
 * completa para poder filtrar "todo lo que cuelga de acá" con una sola consulta.
 * `nivel` es la profundidad: el 0 es el dueño del sistema y ve todo.
 */
@Service()
export class ClientesHttp extends CatalogoHttp<ICliente> {
  protected readonly ruta = '/clientes';

  /** El cliente del permiso con el que se entró. */
  propio(): Promise<ICliente> {
    return firstValueFrom(this.http.get<ICliente>(`${this.ruta}/propio`));
  }

  /**
   * Cuelga el cliente de otro padre.
   *
   * No es un `update` del campo: mover un cliente implica recalcular los
   * `idsAncestros` de él y de toda su descendencia, y eso lo hace el backend.
   */
  cambiarPadre(id: string, idPadre: string): Promise<ICliente> {
    return firstValueFrom(
      this.http.put<ICliente>(`${this.ruta}/cambiarHijosDePadre/${id}/${idPadre}`, {}),
    );
  }

  /** Mayorista, Minorista o Final. Va por su ruta: cambia qué puede colgar debajo. */
  cambiarTipo(id: string, tipoCliente: ITipoCliente): Promise<ICliente> {
    return firstValueFrom(
      this.http.put<ICliente>(`${this.ruta}/cambiarTipo/${id}`, { tipoCliente }),
    );
  }

  /**
   * Borra el cliente. Con `idClienteAPasar`, sus entidades quedan en ese otro
   * en vez de irse con él.
   */
  eliminarPasando(id: string, idClienteAPasar?: string): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${this.ruta}/${id}`, {
        params: idClienteAPasar ? { idClienteAPasar } : undefined,
      }),
    );
  }
}

/**
 * Zonas.
 *
 * Una zona de alumbrado es una `IUbicacion` con `categoria: 'Luminarias'`. **No
 * tiene FK a las luminarias**: el vínculo es puramente geométrico, igual que en
 * las zonas de alarmas. Una luminaria "está en la zona" porque sus coordenadas
 * caen adentro, no porque alguien la haya asignado.
 */
@Service()
export class UbicacionesHttp extends CatalogoHttp<IUbicacion> {
  protected readonly ruta = '/ubicacions';
}

export interface Geocodificacion {
  direccion?: string;
  coordenadas?: [number, number];
}

/**
 * Direcciones.
 *
 * El backend cachea: por eso la ruta es `geocode-cache` y no la del proveedor.
 * **La precisión importa**: para una luminaria se pide 9, que es a nivel de
 * poste; con la precisión global la dirección cae al centro de la manzana y dos
 * luminarias de la misma cuadra terminan con la misma dirección.
 */
@Service()
export class GeocodificacionHttp {
  private readonly http = inject(HttpClient);

  /** Coordenadas → dirección. Precisión 9 = luminaria. */
  inversa(coordenadas: [number, number], precision = 9): Promise<Geocodificacion> {
    return firstValueFrom(
      this.http.post<Geocodificacion>('/geocode-cache/reverse', { coordenadas, precision }),
    );
  }

  /**
   * Dirección → coordenadas.
   *
   * **El backend no devuelve un objeto**: devuelve la tupla `[lng, lat]` pelada,
   * y `[0, 0]` cuando el proveedor no encontró nada (no un 404). Se normaliza
   * acá para que los llamadores lean siempre `coordenadas`: leyendo
   * `res.coordenadas` de la tupla daba `undefined` y toda búsqueda terminaba en
   * "No se encontró esa dirección".
   */
  async directa(texto: string): Promise<Geocodificacion> {
    const res = await firstValueFrom(
      this.http.post<unknown>('/geocode-cache/geocode', { text: texto }),
    );
    return { direccion: texto, coordenadas: comoCoordenadas(res) };
  }

  /**
   * Sugerencias de direcciones mientras se escribe. `cerca` ([lng, lat]) las
   * prioriza alrededor de ese punto, que suele ser el centro del mapa.
   */
  async sugerencias(texto: string, cerca?: [number, number]): Promise<string[]> {
    const res = await firstValueFrom(
      this.http.post<{ resultados?: string[] }>('/geocode-cache/autocomplete', {
        text: texto,
        coordenadas: cerca,
      }),
    );
    return res?.resultados ?? [];
  }
}

/** La tupla del backend (o un `{ coordenadas }`, por las dudas) → `[lng, lat]` válido. */
function comoCoordenadas(res: unknown): [number, number] | undefined {
  const par = Array.isArray(res) ? res : (res as { coordenadas?: unknown } | null)?.coordenadas;
  if (!Array.isArray(par) || par.length < 2) return undefined;
  const lng = Number(par[0]);
  const lat = Number(par[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat) || (lng === 0 && lat === 0)) return undefined;
  return [lng, lat];
}
